import type { RequestAction } from "trystero";
import type { ElectionCertificate, RecoveryStateMachine } from "./recovery-protocol.js";
import { decodeBoundedRoomPacket, encodeRoomPacket } from "./room-packet.js";
import type { OnlineRoomAuthority } from "./room-protocol.js";

interface HandoffPeer {
  readonly peerId: string;
}

export async function initiateRecoveryHandoff(
  state: RecoveryStateMachine,
  authority: OnlineRoomAuthority | undefined,
  peer: HandoffPeer | undefined,
  nextHostMemberId: string,
  action: RequestAction<string, string>,
): Promise<{
  readonly certificate: ElectionCertificate;
  readonly peerId: string;
  readonly gameTimeMs: number;
}> {
  if (!authority) throw new TypeError("Only the current host can hand off");
  if (!peer) throw new TypeError("Handoff target is not connected");
  const certificate = state.prepareHandoff(nextHostMemberId);
  const reply = decodeBoundedRoomPacket<{ readonly ok: boolean; readonly reason?: string }>(
    await action.request(encodeRoomPacket(certificate), {
      target: peer.peerId,
      timeoutMs: 5_000,
    }),
  );
  if (!reply.ok) throw new TypeError(reply.reason ?? "Host handoff was rejected");
  const bundle = state.installElection(certificate, Date.now());
  return { certificate, peerId: peer.peerId, gameTimeMs: bundle.checkpoint.gameTimeMs };
}

export function acceptRecoveryHandoff(
  state: RecoveryStateMachine,
  raw: string,
  selfMemberId: string,
  recover: (
    checkpoint: ReturnType<RecoveryStateMachine["installElection"]>["checkpoint"],
    authorityEpoch: number,
  ) => OnlineRoomAuthority,
): {
  readonly certificate: ElectionCertificate;
  readonly authority: OnlineRoomAuthority;
  readonly gameTimeMs: number;
} {
  const certificate = decodeBoundedRoomPacket<ElectionCertificate>(raw);
  if (certificate.hostMemberId !== selfMemberId)
    throw new TypeError("Host handoff names another member");
  const bundle = state.installElection(certificate, Date.now());
  return {
    certificate,
    authority: recover(bundle.checkpoint, certificate.authorityEpoch),
    gameTimeMs: bundle.checkpoint.gameTimeMs,
  };
}
